<template>
    <v-container>
        <v-scrollable
            class="snap-x scroll-px-sm px-sm md:scroll-px-0 md:px-0">
            <template #header="{ move }">
                <v-scrollable-header class="relative z-10 mx-sm mb-[-4.5rem] md:mx-0"
                    @move="move" />
            </template>

            <div>
                <div ref="titleTrack"
                    class="flex h-12 flex-nowrap items-center space-x-xs md:space-x-sm">
                    <v-h4 class="sticky left-0 w-[208px] py-1 md:w-[592px]">{{ t('budget') }}</v-h4>

                    <v-h4 class="sticky left-0 w-[160px] bg-white py-1 md:w-[260px]">{{ t('better') }}</v-h4>
                </div>

                <div ref="productsTrack"
                    class="mt-6 flex flex-nowrap space-x-xs md:space-x-sm">
                    <v-scrollable-item v-for="product, index in productsBefore"
                        :key="product.key"
                        class="w-[160px] shrink-0 snap-start md:w-[260px]"
                        :class="{
                            'hidden md:block': index > 0,
                        }"
                    >
                        <v-elevate-product-card :product="product" />
                    </v-scrollable-item>

                    <div class="flex w-10 shrink-0 justify-center border-gray-200">
                        <div class="h-[160px] rounded-full border border-gray-100 md:h-[260px]"></div>
                    </div>

                    <v-scrollable-item v-for="product in productsAfter"
                        :key="product.key"
                        class="w-[160px] shrink-0 snap-start md:w-[260px]"
                    >
                        <v-elevate-product-card :product="product" />
                    </v-scrollable-item>
                </div>
            </div>
        </v-scrollable>
    </v-container>
</template>

<script lang="ts" setup>
import type { ProductPage } from '@apptus/esales-api';
import { useWindowSize, watchDebounced } from '@vueuse/core';

const properties = defineProps<{
    before?: ProductPage['recommendationLists'][0]
    after?: ProductPage['recommendationLists'][0]
}>();

const { t } = useI18n();
const { width } = useWindowSize();

const productsBefore = computed(() => properties.before?.productGroups.flatMap((group) => group.products) || []);
const productsAfter = computed(() => properties.after?.productGroups.flatMap((group) => group.products) || []);

const productsTrack = ref<HTMLDivElement>();
const titleTrack = ref<HTMLDivElement>();

function setTitleTrackWidth(trackWidth: number) {
    titleTrack.value?.style.setProperty('width', `${trackWidth}px`);
}

onMounted(() => {
    if (productsTrack.value) {
        setTitleTrackWidth(productsTrack.value?.scrollWidth);
    }
});

watchDebounced(width, () => {
    if (productsTrack.value) {
        setTitleTrackWidth(productsTrack.value?.scrollWidth);
    }
}, { debounce: 100 });
</script>

<i18n lang="json">
{
    "et": {
        "product-nosto-title": "Kas otsite midagi paremat?",
        "budget": "Taskukohane",
        "better": "Eksklusiivne"
    },
    "fi": {
        "product-nosto-title": "Etsitkö jotain parempaa?",
        "budget": "Pihistä",
        "better": "Panosta"
    }
}
</i18n>
